import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { useRestoreValidatedPersona } from '@/hooks/use-personas';

interface PersonaRestoreNoticeProps {
	personaId: string;
}

/** Shown while a newer version waits for validation: the way back to the version last validated. */
export function PersonaRestoreNotice({ personaId }: PersonaRestoreNoticeProps) {
	const [confirmOpen, setConfirmOpen] = useState(false);
	const restore = useRestoreValidatedPersona();

	const handleRestore = async () => {
		try {
			await restore.mutateAsync({ id: personaId });
			toast.success('Restored your last validated version');
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Failed to restore the validated version');
		}
	};

	return (
		<>
			<div className='flex items-center justify-between gap-3 rounded-2xl border bg-gray-50 p-3'>
				<p className='text-xs text-gray-900'>
					The AI keeps using your last validated version until you validate this one. Not happy with it?
				</p>
				<Button
					variant='outline'
					size='sm'
					className='shrink-0 font-normal'
					disabled={restore.isPending}
					onClick={() => setConfirmOpen(true)}
				>
					<RotateCcw className='size-3.5' />
					Restore validated version
				</Button>
			</div>

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Restore the last validated version?</AlertDialogTitle>
						<AlertDialogDescription>
							Your persona goes back to the version you last validated, and the changes made since then are undone.
							The AI already uses that version, so replies don't change.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={handleRestore}>Restore</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
